import { useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

/**
 * Phase 3 - iOS/Telegram large-title header for top-level tabs.
 *
 * Renders inside the page scroller so the title scrolls away naturally.
 * Uses an IntersectionObserver to inform the sticky compact header when
 * to fade in the inline centered title.
 *
 * Single source of truth via a CustomEvent so we don't need to wire a
 * context through the existing layout.
 */
const TAB_TITLE_KEYS: Record<string, { ns: string; key: string }> = {
  // Home/Dashboard intentionally omitted - greeting + balance act as the large title
  "/connect-portal/contacts": { ns: "common", key: "contacts" },
  "/connect-portal/dial": { ns: "common", key: "call" },
  "/connect-portal/history": { ns: "common", key: "history" },
  "/connect-portal/me": { ns: "common", key: "account" },
  "/connect-portal/topup": { ns: "common", key: "wallet" },
};

export interface LargeTitleEventDetail {
  visible: boolean;
  title: string;
}

export const LARGE_TITLE_EVENT = "elefa:large-title";

export function NativeLargeTitle() {
  const location = useLocation();
  const { t } = useTranslation("common");
  const ref = useRef<HTMLDivElement>(null);

  const meta = useMemo(() => {
    const path = location.pathname.replace(/\/$/, "");
    return TAB_TITLE_KEYS[path] ?? null;
  }, [location.pathname]);

  const title = meta ? t(meta.key) : "";

  // Tell the sticky header whether the inline title is visible
  useEffect(() => {
    if (!ref.current) return;
    const node = ref.current;
    const dispatch = (visible: boolean) => {
      window.dispatchEvent(
        new CustomEvent<LargeTitleEventDetail>(LARGE_TITLE_EVENT, {
          detail: { visible, title },
        }),
      );
    };
    // Initial fire - large title is visible at mount
    dispatch(true);
    const observer = new IntersectionObserver(
      ([entry]) => dispatch(entry.isIntersecting),
      { threshold: 0, rootMargin: "-8px 0px 0px 0px" },
    );
    observer.observe(node);
    return () => {
      observer.disconnect();
      // Reset compact header on unmount
      dispatch(true);
    };
  }, [title]);

  // Title is now rendered inline in the sticky ConnectPortalHeader banner
  // for Recents/Contacts/Keypad-style consistency across every tab.
  // We keep this component as a no-op so existing layout wiring still works
  // and external imports remain valid.
  return null;
}

export default NativeLargeTitle;
